"use client";

import { useState, useEffect, useCallback } from "react";
import { TrendingUp, Target } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";

interface XTrackerProgressChartProps {
  handle?: string;
  title?: string;
  startDate: string;
  endDate: string;
  targetCount?: number;
}

interface Metric {
  date: string;
  count: number;
  cumulative: number;
}

export default function XTrackerProgressChart({
  handle = "elonmusk",
  title,
  startDate,
  endDate,
  targetCount,
}: XTrackerProgressChartProps) {
  const [hourlyMetrics, setHourlyMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const visRef = useVisibilityRef();

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ handle, startDate, endDate });
      if (title) params.set("title", title);
      const res = await fetch(`/api/xtracker?${params.toString()}`);
      if (res.ok) {
        const result = await res.json();
        // Use hourly data for accurate cumulative chart, fallback to daily
        setHourlyMetrics(result.metrics?.hourly || result.metrics?.daily || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [handle, title, startDate, endDate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => { if (visRef.current) fetchData(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 h-48 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 dark:bg-white/10 rounded mb-4" />
        <div className="h-full bg-gray-100 dark:bg-white/5 rounded-[5px]" />
      </div>
    );
  }

  if (hourlyMetrics.length === 0) {
    return null;
  }

  const currentTotal = hourlyMetrics[hourlyMetrics.length - 1]?.cumulative || 0;
  const maxValue = targetCount ? Math.max(targetCount, currentTotal) : currentTotal;
  const chartHeight = 120;
  const chartWidth = 100; // percentage

  // Create path for the area chart using all hourly data points
  const points = hourlyMetrics.map((m, i) => {
    const x = (i / (hourlyMetrics.length - 1)) * 100;
    const y = chartHeight - (m.cumulative / maxValue) * chartHeight;
    return { x, y, ...m };
  });

  // SVG path for the line
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  // SVG path for the filled area
  const areaPath = `${linePath} L ${100} ${chartHeight} L 0 ${chartHeight} Z`;

  // Target line Y position
  const targetY = targetCount ? chartHeight - (targetCount / maxValue) * chartHeight : null;

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs">
            <TrendingUp className="w-4 h-4" />
            <span className="font-medium">Cumulative Progress</span>
          </div>
          {targetCount && (
            <div className="flex items-center gap-1.5 text-xs">
              <Target className="w-3 h-3 text-gray-500 dark:text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400">{targetCount.toLocaleString()} target</span>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="px-4 pb-2">
        <div className="relative text-gray-900 dark:text-white" style={{ height: `${chartHeight}px` }}>
          <svg
            viewBox={`0 0 100 ${chartHeight}`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            {/* Gradient definition */}
            <defs>
              <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            {[0.25, 0.5, 0.75].map((ratio) => (
              <line
                key={ratio}
                x1="0"
                y1={chartHeight * ratio}
                x2="100"
                y2={chartHeight * ratio}
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeDasharray="2,2"
              />
            ))}

            {/* Target line */}
            {targetY !== null && (
              <>
                <line
                  x1="0"
                  y1={targetY}
                  x2="100"
                  y2={targetY}
                  stroke="currentColor"
                  strokeOpacity="0.5"
                  strokeDasharray="4,4"
                  strokeWidth="1"
                />
              </>
            )}

            {/* Area fill */}
            <path d={areaPath} fill="url(#chartGradient)" />

            {/* Line */}
            <path
              d={linePath}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />

            {/* Current point */}
            {points.length > 0 && (
              <circle
                cx={points[points.length - 1].x}
                cy={points[points.length - 1].y}
                r="4"
                fill="currentColor"
                stroke="white"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[9px] text-gray-500 dark:text-gray-600 -ml-1">
            <span>{maxValue.toLocaleString()}</span>
            <span>{Math.round(maxValue / 2).toLocaleString()}</span>
            <span>0</span>
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="px-4 pb-3 flex justify-between text-[9px] text-gray-500 dark:text-gray-600">
        <span>{formatDate(hourlyMetrics[0]?.date || startDate)}</span>
        <span>{formatDate(hourlyMetrics[hourlyMetrics.length - 1]?.date || endDate)}</span>
      </div>

      {/* Stats footer */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-white/[0.02] flex items-center justify-between">
        <div className="text-xs">
          <span className="text-gray-500">Current: </span>
          <span className="text-gray-900 dark:text-white font-bold">{currentTotal.toLocaleString()}</span>
        </div>
        {targetCount && (
          <div className="text-xs">
            <span className="text-gray-500">Remaining: </span>
            <span className={currentTotal >= targetCount ? "text-[#00FF9D] font-bold" : "text-gray-900 dark:text-white font-bold"}>
              {currentTotal >= targetCount ? "Target reached!" : (targetCount - currentTotal).toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
